/**
 * UltrasoundErrorBoundary Component
 * 
 * Error boundary specifically designed for ultrasound components to catch and handle
 * JavaScript errors that occur during rendering, in lifecycle methods, and in constructors
 * of the whole tree below them.
 */

import React, { Component, ReactNode } from 'react';
import Card from '@amzn/meridian/card';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';

interface UltrasoundErrorBoundaryProps {
  children: ReactNode;
  /** Optional callback when error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Optional fallback component to render instead of default error UI */
  fallback?: ReactNode;
}

interface UltrasoundErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error boundary component for ultrasound section to catch and handle
 * component failures gracefully without breaking the entire application.
 */
export class UltrasoundErrorBoundary extends Component<
  UltrasoundErrorBoundaryProps,
  UltrasoundErrorBoundaryState
> {
  constructor(props: UltrasoundErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<UltrasoundErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details
    console.error('UltrasoundErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      errorInfo
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * Reset error boundary state to retry rendering
   */
  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Card>
          <Column spacing="300">
            <Text type="h100">Ultrasound Component Error</Text>
            <Alert type="error">
              <Column spacing="200">
                <Text type="b200">
                  An error occurred while loading the ultrasound components.
                  This may be due to a network issue or a temporary service problem.
                </Text>

                {this.state.error && (
                  <Text type="b100" color="secondary">
                    Error: {this.state.error.message}
                  </Text>
                )}

                <Button
                  type="secondary"
                  size="small"
                  onClick={this.handleRetry}
                >
                  Retry
                </Button>
              </Column>
            </Alert>
          </Column>
        </Card>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}